
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import type { ShelterEvent, EventAttendee } from '../../utils/supabase';

interface AttendeeWithDetails extends EventAttendee {
  has_phone: boolean;
  has_email: boolean;
}

export default function EventStatistics() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState<ShelterEvent | null>(null);
  const [shelter] = useState<any>(null);
  const [attendees, setAttendees] = useState<AttendeeWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Verificar sesión
  useEffect(() => {
    const sessionData = localStorage.getItem('user_session');
    if (!sessionData) {
      navigate('/login');
      return;
    }

    try {
      const session = JSON.parse(sessionData);
      const now = Date.now();
      if (now > session.expiresAt) {
        localStorage.removeItem('user_session');
        navigate('/login');
        return;
      }
      setCurrentUser(session);
    } catch {
      localStorage.removeItem('user_session');
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (eventId && currentUser) {
      loadEventStatistics();
    }
  }, [eventId, currentUser]);

  const loadEventStatistics = async () => {
    try {
      // Cargar evento
      const { data: eventData, error: eventError } = await supabase
        .from('shelter_events')
        .select('*')
        .eq('id', eventId)
        .eq('user_id', currentUser.id)
        .single();

      if (eventError) throw eventError;
      if (!eventData) {
        navigate('/admin');
        return;
      }

      setEvent(eventData);

      // Cargar asistentes
      const { data: attendeesData, error: attendeesError } = await supabase
        .from('event_attendees')
        .select('*')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });

      if (attendeesError) throw attendeesError;

      // Procesar datos de asistentes
      const processedAttendees = (attendeesData || []).map(attendee => ({
        ...attendee,
        has_phone: Boolean(attendee.attendee_phone && attendee.attendee_phone.trim()),
        has_email: Boolean(attendee.attendee_email && attendee.attendee_email.trim())
      }));

      setAttendees(processedAttendees);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  // Función para determinar el estado del evento
  const getEventStatus = () => {
    if (!event) return { status: '', className: '' };
    
    const eventDate = new Date(event.event_date);
    const now = new Date();
    const oneDayAfterEvent = new Date(eventDate.getTime() + 24 * 60 * 60 * 1000); // 24 horas después
    
    const isToday = eventDate.toDateString() === now.toDateString();
    const isTomorrow = eventDate.toDateString() === new Date(now.getTime() + 24 * 60 * 60 * 1000).toDateString();
    const isEventCompleted = now > oneDayAfterEvent; // Cambio: usar oneDayAfterEvent en lugar de eventDate
    const isUpcoming = eventDate > now;
    
    if (isEventCompleted) {
      return { 
        status: 'EVENTO REALIZADO', 
        className: 'bg-gray-500 text-white' 
      };
    } else if (isToday) {
      return { 
        status: '¡HOY!', 
        className: 'bg-red-500 text-white' 
      };
    } else if (isTomorrow) {
      return { 
        status: 'MAÑANA', 
        className: 'bg-orange-500 text-white' 
      };
    } else if (isUpcoming) {
      return { 
        status: 'PRÓXIMO', 
        className: 'bg-green-500 text-white' 
      };
    } else {
      return { 
        status: 'EVENTO REALIZADO', 
        className: 'bg-gray-500 text-white' 
      };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando estadísticas...</p>
        </div>
      </div>
    );
  }

  if (!event || !shelter) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-gray-400 mb-4"></i>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Evento no encontrado</h2>
          <p className="text-gray-600 mb-6">El evento que buscas no existe o ha sido eliminado.</p>
          <button 
            onClick={() => navigate('/admin')}
            className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Volver al Panel
          </button>
        </div>
      </div>
    );
  }

  const eventDate = new Date(event.event_date);
  const eventStatus = getEventStatus();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <button 
              onClick={() => navigate('/admin')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="font-medium">Volver al Panel</span>
            </button>
            
            <div className="flex items-center space-x-3">
              <img 
                src="/images/image.png" 
                alt="Huellitas Unidas Logo" 
                className="w-8 h-8 object-contain"
              />
              <h1 className="text-lg font-bold text-gray-800" style={{ fontFamily: '"Pacifico", serif' }}>
                Huellitas Unidas
              </h1>
            </div>

            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/')}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-home-line text-xl text-gray-600"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">{event.title}</h1>
              <p className="text-gray-600 mt-2">Estadísticas y análisis del evento</p>
            </div>
            <div className={`px-4 py-2 rounded-full text-sm font-bold ${eventStatus.className}`}>
              {eventStatus.status}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Event Date Card and Stats */}
          <div className="lg:col-span-2 space-y-8">
            {/* Event Date Card - Rediseñada */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center justify-center">
                <div className="text-center">
                  {/* Icono de calendario grande */}
                  <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                    <i className="ri-calendar-event-fill text-white text-4xl"></i>
                  </div>
                  
                  {/* Fecha y hora */}
                  <div className="space-y-2 mb-6">
                    <div className="text-4xl font-bold text-gray-900">
                      {eventDate.toLocaleDateString('es-ES', { day: 'numeric' })}
                    </div>
                    <div className="text-2xl font-semibold text-purple-600">
                      {eventDate.toLocaleDateString('es-ES', { month: 'long' })}
                    </div>
                    <div className="text-lg text-gray-600">
                      {eventDate.getFullYear()}
                    </div>
                    <div className="text-sm font-bold text-gray-800 uppercase tracking-wider">
                      {eventDate.toLocaleDateString('es-ES', { weekday: 'long' })}
                    </div>
                    <div className="text-xl font-semibold text-indigo-600">
                      {eventDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  
                  {/* Ubicación */}
                  <div className="flex items-center justify-center text-gray-600 mb-4">
                    <i className="ri-map-pin-fill mr-2 text-red-500"></i>
                    <span className="font-medium">{event.location}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-group-fill text-blue-500 text-2xl"></i>
                </div>
                <div className="text-3xl font-bold text-blue-600 mb-2">{attendees.length}</div>
                <div className="text-sm text-gray-600">Total Confirmados</div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-phone-fill text-green-500 text-2xl"></i>
                </div>
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {attendees.filter(a => a.attendee_phone).length}
                </div>
                <div className="text-sm text-gray-600">
                  Con Teléfono ({Math.round((attendees.filter(a => a.attendee_phone).length / Math.max(attendees.length, 1)) * 100)}%)
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-mail-fill text-orange-500 text-2xl"></i>
                </div>
                <div className="text-3xl font-bold text-orange-600 mb-2">
                  {attendees.filter(a => a.attendee_email).length}
                </div>
                <div className="text-sm text-gray-600">
                  Con Email ({Math.round((attendees.filter(a => a.attendee_email).length / Math.max(attendees.length, 1)) * 100)}%)
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-double-fill text-purple-500 text-2xl"></i>
                </div>
                <div className="text-3xl font-bold text-purple-600 mb-2">
                  {attendees.filter(a => a.attendee_phone && a.attendee_email).length}
                </div>
                <div className="text-sm text-gray-600">Datos Completos</div>
              </div>
            </div>

            {/* Contact Information Distribution Chart */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Distribución de Información de Contacto</h3>
              
              <div className="space-y-4">
                {/* Complete Information */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                    <span className="text-gray-700">Información Completa (Nombre + Teléfono + Email)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-48 bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-green-500 h-3 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${attendees.length > 0 ? (attendees.filter(a => a.attendee_phone && a.attendee_email).length / attendees.length) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-600 w-12">
                      {attendees.filter(a => a.attendee_phone && a.attendee_email).length}
                    </span>
                  </div>
                </div>

                {/* Partial Information */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                    <span className="text-gray-700">Información Parcial (Solo Teléfono o Email)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-48 bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-yellow-500 h-3 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${attendees.length > 0 ? (attendees.filter(a => (a.attendee_phone || a.attendee_email) && !(a.attendee_phone && a.attendee_email)).length / attendees.length) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-600 w-12">
                      {attendees.filter(a => (a.attendee_phone || a.attendee_email) && !(a.attendee_phone && a.attendee_email)).length}
                    </span>
                  </div>
                </div>

                {/* Basic Information */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 bg-gray-400 rounded-full"></div>
                    <span className="text-gray-700">Solo Nombre</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-48 bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-gray-400 h-3 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${attendees.length > 0 ? (attendees.filter(a => !a.attendee_phone && !a.attendee_email).length / attendees.length) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-600 w-12">
                      {attendees.filter(a => !a.attendee_phone && !a.attendee_email).length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Attendees List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Lista de Asistentes Registrados</h3>
              
              {attendees.length === 0 ? (
                <div className="text-center py-8">
                  <i className="ri-group-line text-4xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500">Aún no hay asistentes registrados</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {attendees.map((attendee, index) => (
                    <div key={attendee.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-semibold">
                            #{index + 1}
                          </span>
                          <h4 className="font-semibold text-gray-900">{attendee.attendee_name}</h4>
                        </div>
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
                          Registrado
                        </span>
                      </div>
                      
                      <div className="space-y-1 text-sm">
                        {attendee.attendee_phone && (
                          <div className="flex items-center text-gray-600">
                            <i className="ri-phone-line mr-2 text-green-500"></i>
                            <span>{attendee.attendee_phone}</span>
                          </div>
                        )}
                        
                        {attendee.attendee_email && (
                          <div className="flex items-center text-gray-600">
                            <i className="ri-mail-line mr-2 text-blue-500"></i>
                            <span>{attendee.attendee_email}</span>
                          </div>
                        )}
                        
                        <div className="flex items-center text-gray-500 text-xs mt-2">
                          <i className="ri-time-line mr-1"></i>
                          <span>
                            {new Date(attendee.created_at).toLocaleDateString('es-ES', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
